import { useQuery } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui';
import { curationApi } from '@/lib/api';

interface EntitySearchProps {
  onPick: (iri: string, label?: string) => void;
}

/**
 * Lightweight search box used as an entry-point picker. Reuses the
 * `/curation/entities/search` autocomplete endpoint — it already does
 * substring matching over `entity_labels` and is fast enough for live typing.
 */
export function EntitySearch({ onPick }: EntitySearchProps) {
  const { t } = useTranslation(['studio', 'common']);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const tm = setTimeout(() => setDebounced(search), 180);
    return () => clearTimeout(tm);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['explorer', 'search', debounced],
    queryFn: () => curationApi.searchEntities(debounced),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  return (
    <div className="relative w-80">
      <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" aria-hidden />
      <Input
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={t('studio:explorer.searchPlaceholder')}
        className="pl-8"
      />
      {open && debounced.length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg">
          {isLoading && (
            <div className="flex items-center gap-2 p-2 text-small text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              {t('common:status.loading')}
            </div>
          )}
          {!isLoading && data && data.length === 0 && (
            <div className="p-2 text-small text-slate-500">{t('common:status.noResults')}</div>
          )}
          {!isLoading &&
            data?.map((hit) => (
              <button
                key={hit.entity_iri}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(hit.entity_iri, hit.label);
                  setOpen(false);
                  setSearch(hit.label);
                }}
                className="block w-full px-2 py-1.5 text-left hover:bg-slate-50"
              >
                <div className="text-small font-medium text-slate-800">{hit.label}</div>
                <div className="truncate font-mono text-small text-slate-500">
                  {hit.entity_iri}
                </div>
                <div className="text-small text-slate-400">{hit.entity_class}</div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
